import {
  Avatar,
  CircularProgress,
  IconButton,
  LinearProgress,
  Link as ButtonLink,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import BlockIcon from '@mui/icons-material/Block';
import CheckIcon from '@mui/icons-material/Check';
import React, { useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';
import { reduxForm } from 'redux-form/immutable';
import { Box, Button, Checkbox, TableSortLabel, Toolbar } from '@mui/material';

import { TableBox } from 'retired/Box';
import NonprofitLink from 'retired/NonprofitLink';
import PageHeader from 'retired/PageHeader';
import TablePaginationActions from 'retired/TablePaginationActions';
import config from 'config';
import Api from 'entities/api';
import Donation from 'entities/donation/model';
import {
  selectAcceptedExternalDonations,
  selectDeniedExternalDonations,
  selectPendingExternalDonations,
  selectPendingExternalTotal,
} from 'entities/donation/selectors';
import { selectLocations } from 'entities/location/selectors';
import { selectOrganization, selectUser, selectUserLocations } from 'entities/user/selectors';
import { dark, gray3, gray5, primaryBlue, red } from 'utils/color-styles';
import { currenciesFormat } from 'utils/currencyFormat';
import { useInjectEpics } from 'utils/injectEpics';
import { useInjectReducer } from 'utils/injectReducer';
import { Features } from 'utils/features';
import { truthy } from 'utils/truthy';

import { Privileges } from '../../utils/privileges';

import epics from './epics';
import { ExternalDonationRequestModal } from './ExternalDonationRequestModal';
import { ExternalDonationRequestsFilters } from './ExternalDonationRequestsFilters';
import reducer from './reducer';
import {
  selectDisplayedDonations,
  selectError,
  selectFetchParams,
  selectLoading,
  selectStatusUpdating,
  selectTotal,
} from './selectors';
import {
  approveAction,
  denyAction,
  FORM_IDENTIFIER,
  initAction,
  MATCHING_REQUEST_STATUS_DISPLAY_KEYS,
  MATCHING_REQUEST_STATUSES,
  setFetchParamsAction,
} from './types';
import { formatDateWithoutTimezone, formatDonationDateForMatchingRequestStatus } from './utils';
import { RejectExternalDonationModal } from './RejectExternalDonationModal';
import { ConfidenceScoreChip } from './ConfidenceScoreChip';
import { SearchInput } from './SearchInput';

const styles = {
  nameLink: {
    width: 110,
    display: 'block',
    lineHeight: '32px',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
};

const onClickHandler = (e: MouseEvent) => e.stopPropagation();

const TabItem = ({ children, isActive, ...rest }): JSX.Element => {
  const normal = {
    color: gray5,
    fontWeight: 400,
    fontSize: 14,
    marginTop: 15,
    marginBottom: 0,
    marginRight: 25,
    paddingBottom: 14,
    cursor: 'pointer',
  };
  const active = {
    ...normal,
    ...{
      color: dark,
      borderBottom: `1px solid ${dark}`,
      fontWeight: 500,
    },
  };

  return (
    <span style={isActive ? active : normal} {...rest}>
      {children}
    </span>
  );
};

const ExternalDonationRequests: React.FC<{}> = () => {
  const dispatch = useDispatch();
  useInjectEpics({ key: 'externalDonationRequests', epics });
  useInjectReducer({ key: 'externalDonationRequests', reducer });

  const pendingExternalDonations = useSelector(selectPendingExternalDonations);
  const acceptedExternalDonations = useSelector(selectAcceptedExternalDonations);
  const deniedExternalDonations = useSelector(selectDeniedExternalDonations);
  const statusUpdating = useSelector(selectStatusUpdating);
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);
  const organization = useSelector(selectOrganization);
  const user = useSelector(selectUser);
  const history = useHistory();
  const userLocations = useSelector(selectUserLocations);
  const locations = useSelector(selectLocations);
  const allLocations = userLocations.concat(locations);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  const {
    matchingRequestStatus,
  }: {
    matchingRequestStatus: MATCHING_REQUEST_STATUS_DISPLAY_KEYS;
  } = useParams();
  const displayedDonations = useSelector(selectDisplayedDonations);
  const fetchParams = useSelector(selectFetchParams);
  const totalCount = useSelector(selectTotal);
  const pendingExternalTotal = useSelector(selectPendingExternalTotal);

  const showApproveDeny =
    MATCHING_REQUEST_STATUSES[matchingRequestStatus] === MATCHING_REQUEST_STATUSES.pending;

  useEffect(() => {
    dispatch(initAction());
  }, []);

  useEffect(() => {
    if (
      user &&
      !(
        user.hasPrivilege(Privileges.DONATION_MATCH) &&
        user.hasFeature(Features.EXTERNAL_DONATIONS) &&
        user.hasFeature(Features.EXTERNAL_MATCHING)
      )
    ) {
      history.replace('/');
    }
  });

  useEffect(() => {
    let sort;

    switch (matchingRequestStatus) {
      case MATCHING_REQUEST_STATUS_DISPLAY_KEYS.approved: {
        sort = 'matchedAt';
        break;
      }

      case MATCHING_REQUEST_STATUS_DISPLAY_KEYS.denied: {
        sort = 'matchRequestRejectedAt';
        break;
      }

      case MATCHING_REQUEST_STATUS_DISPLAY_KEYS.pending:
      default: {
        sort = 'createdAt';
        break;
      }
    }

    dispatch(
      setFetchParamsAction({
        matchingRequestStatus: MATCHING_REQUEST_STATUSES[matchingRequestStatus],
        sort,
        order: 'desc',
      })
    );
  }, [matchingRequestStatus]);

  const anyStatusUpdating =
    Boolean(Object.values(Object.fromEntries(statusUpdating)).filter(Boolean).length) || loading;

  let donations;
  let relevantDonationMatchAmountField: string;
  let relevantDonationMatchingRuleField: string;
  let relevantDonationMatchAmountCurrenciesField: string;

  switch (MATCHING_REQUEST_STATUSES[matchingRequestStatus]) {
    case MATCHING_REQUEST_STATUSES.approved: {
      donations = acceptedExternalDonations;
      relevantDonationMatchAmountField = 'matchedAmount';
      relevantDonationMatchingRuleField = '';
      relevantDonationMatchAmountCurrenciesField = 'matchedAmountCurrencies';
      break;
    }

    case MATCHING_REQUEST_STATUSES.denied: {
      donations = deniedExternalDonations;
      relevantDonationMatchAmountField = '';
      relevantDonationMatchingRuleField = '';
      relevantDonationMatchAmountCurrenciesField = '';
      break;
    }

    case MATCHING_REQUEST_STATUSES.pending:
    default: {
      donations = pendingExternalDonations;
      relevantDonationMatchAmountField = 'matchableAmount';
      relevantDonationMatchingRuleField = 'matchableRule';
      relevantDonationMatchAmountCurrenciesField = 'matchableAmountCurrencies';
      break;
    }
  }

  const selectedItemsForCurrentPage = selectedItems.filter((item) =>
    displayedDonations?.some((donation) => donation.id === item)
  );

  const handleCheckboxChange = (id: string) => {
    setSelectedItems((prevSelectedItems) => {
      if (prevSelectedItems.includes(id)) {
        return prevSelectedItems.filter((itemId) => itemId !== id);
      } else {
        return [...prevSelectedItems, id];
      }
    });
  };

  const selectableDisplayedDonations = displayedDonations.filter(
    (donation) => donation?.matchableAmount && donation?.user?.email
  );

  const handleSelectAll = () => {
    const isAllSelectedForCurrentPage =
      selectedItemsForCurrentPage.length === selectableDisplayedDonations?.size;

    if (isAllSelectedForCurrentPage) {
      // if all selected for current page then deselect all from the current page
      setSelectedItems(selectedItems.filter((item) => !selectedItemsForCurrentPage.includes(item)));
    } else {
      // if not selected all then select the rest of all for current page
      const notSelectedCurrentPageItems = (selectableDisplayedDonations || [])
        .map((item) => (selectedItemsForCurrentPage.includes(item.id) ? null : item.id))
        .filter(truthy);

      setSelectedItems((prevSelectedItems) => [
        ...prevSelectedItems,
        ...notSelectedCurrentPageItems,
      ]);
    }
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDonationId, setSelectedDonationId] = useState(null);
  const [rejectDonationIds, setRejectDonationIds] = useState<string[]>([]);

  const selectedDonation = donations.get(selectedDonationId);

  const goToPage = (__: unknown, pageNum: number): void => {
    dispatch(
      setFetchParamsAction({
        pageNum,
        matchingRequestStatus: MATCHING_REQUEST_STATUSES[matchingRequestStatus],
      })
    );
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    dispatch(
      setFetchParamsAction({
        limit: Number(event.target.value),
        matchingRequestStatus: MATCHING_REQUEST_STATUSES[matchingRequestStatus],
      })
    );
  };

  const selectSubmission = (matchingStatus: MATCHING_REQUEST_STATUS_DISPLAY_KEYS): void => {
    history.replace(`/donations/external/${matchingStatus}`);
  };

  useEffect(() => {
    if (selectedDonation && !!statusUpdating.size && !statusUpdating.get(selectedDonation.id)) {
      setModalOpen(false);
    }
  }, [loading, statusUpdating]);

  const capitalizedMatchingRequestStatusDisplayKey =
    MATCHING_REQUEST_STATUS_DISPLAY_KEYS[matchingRequestStatus]?.charAt(0)?.toUpperCase() +
    MATCHING_REQUEST_STATUS_DISPLAY_KEYS[matchingRequestStatus]?.slice(1);

  const sortTableBy = (column: string, reserve = false): void => {
    const defaultOrder = reserve ? 'asc' : 'desc';
    const reverseOrder = reserve ? 'desc' : 'asc';
    dispatch(
      setFetchParamsAction({
        sort: column,
        order:
          fetchParams.get('sort') !== column || fetchParams.get('order') === reverseOrder
            ? defaultOrder
            : reverseOrder,
      })
    );
  };

  const showAiScore =
    user?.hasFeature(Features.AI_RECEIPT_READER) &&
    // @NOTE-DP: don't show the AI score for denied donations, because we don't know if they were eligible at the time of rejection
    // If we want to start showing it for denied donations, we need to store the eligibility state at the time of rejection in the database
    MATCHING_REQUEST_STATUSES[matchingRequestStatus] !== MATCHING_REQUEST_STATUSES.denied;

  return (
    <>
      <Helmet
        title={`${capitalizedMatchingRequestStatusDisplayKey} External Donation Match Requests`}
      />
      <PageHeader
        title="External Donation Match Requests"
        subtitle={
          organization
            ? `All employees requesting a match have confirmed they have not received a reward for their donation in accordance with ${organization.name}'s policy.`
            : ''
        }
        onArrowClick={() => history.push('/donations')}
        {...(user?.hasPrivilege(Privileges.EXTERNAL_MATCH_REQUEST_RULES_MANAGE) &&
          user?.hasFeature(Features.EXTERNAL_MATCHING_RULES) && {
            buttonText: 'Rules',
            onButtonClick: () => history.push('/donations/external-match-request-rules'),
          })}
      />
      <div style={{ marginBottom: '1.33em' }}>
        <TabItem
          isActive={
            MATCHING_REQUEST_STATUSES[matchingRequestStatus] === MATCHING_REQUEST_STATUSES.pending
          }
          onClick={(): void => selectSubmission(MATCHING_REQUEST_STATUS_DISPLAY_KEYS.pending)}
        >
          Pending
          {Number.isFinite(pendingExternalTotal) && pendingExternalTotal > 0
            ? ` (${pendingExternalTotal})`
            : ''}
        </TabItem>
        <TabItem
          isActive={
            MATCHING_REQUEST_STATUSES[matchingRequestStatus] === MATCHING_REQUEST_STATUSES.approved
          }
          onClick={(): void => selectSubmission(MATCHING_REQUEST_STATUS_DISPLAY_KEYS.approved)}
        >
          Approved
        </TabItem>
        <TabItem
          isActive={
            MATCHING_REQUEST_STATUSES[matchingRequestStatus] === MATCHING_REQUEST_STATUSES.denied
          }
          onClick={(): void => selectSubmission(MATCHING_REQUEST_STATUS_DISPLAY_KEYS.denied)}
        >
          Denied
        </TabItem>
      </div>
      <ExternalDonationRequestsFilters matchingRequestStatus={matchingRequestStatus} />
      <div>
        <TableBox>
          {loading && <LinearProgress />}
          {showApproveDeny && (
            <Toolbar
              style={{
                justifyContent: 'flex-start',
              }}
            >
              <Box
                style={{
                  marginLeft: 'auto',
                  marginRight: 24,
                }}
              >
                <Button
                  style={{
                    marginLeft: 16,
                    ...(!(selectedItemsForCurrentPage.length < 1 || anyStatusUpdating) && {
                      color: red,
                      border: `1px solid ${red}`,
                    }),
                  }}
                  disabled={selectedItemsForCurrentPage.length < 1 || anyStatusUpdating}
                  onClick={() => {
                    setRejectDonationIds(selectedItemsForCurrentPage);
                  }}
                >
                  Reject {selectedItemsForCurrentPage.length} request
                  {selectedItemsForCurrentPage.length > 1 && 's'}
                  <BlockIcon style={{ marginLeft: 5, alignSelf: 'flex-start' }} />
                </Button>
                <Button
                  style={{
                    marginLeft: 16,
                  }}
                  disabled={selectedItemsForCurrentPage.length < 1 || anyStatusUpdating}
                  onClick={() => {
                    dispatch(approveAction(selectedItemsForCurrentPage));
                  }}
                >
                  Approve {selectedItemsForCurrentPage.length} request
                  {selectedItemsForCurrentPage.length > 1 && 's'}
                  <CheckIcon style={{ marginLeft: 5, alignSelf: 'flex-start' }} />
                </Button>
              </Box>

              <SearchInput />
            </Toolbar>
          )}
          <Table size="small">
            <TableHead>
              <TableRow>
                {showApproveDeny && (
                  <TableCell size="medium">
                    <Checkbox
                      color="primary"
                      checked={selectableDisplayedDonations.every((donation) =>
                        selectedItems.includes(donation.id)
                      )}
                      onChange={handleSelectAll}
                      disabled={anyStatusUpdating}
                    />
                  </TableCell>
                )}
                <TableCell size="medium">Employee</TableCell>
                <TableCell size="medium">Employee Location</TableCell>
                <TableCell size="medium">Nonprofit</TableCell>
                {showAiScore && (
                  <TableCell size="medium">
                    <Tooltip title="The AI confidence level reflects how well this receipt meets donation match criteria. It verifies receipt authenticity by comparing key details—such as the name, donation amount, and donation date—with the user's submitted information, and by analyzing the receipt's structure and details to determine if it's a genuine donation receipt.">
                      <span>AI Confidence Level</span>
                    </Tooltip>
                  </TableCell>
                )}
                <TableCell size="medium">
                  <TableSortLabel
                    direction={fetchParams.get('order')}
                    active={fetchParams.get('sort') === 'date'}
                    onClick={(): void => sortTableBy('date', true)}
                  >
                    Donated on
                  </TableSortLabel>
                </TableCell>
                {showApproveDeny ? (
                  <TableCell size="medium">
                    <TableSortLabel
                      direction={fetchParams.get('order')}
                      active={fetchParams.get('sort') === 'createdAt'}
                      onClick={(): void => sortTableBy('createdAt', true)}
                    >
                      Submitted on
                    </TableSortLabel>
                  </TableCell>
                ) : (
                  <TableCell size="medium">
                    {(MATCHING_REQUEST_STATUSES[matchingRequestStatus] ===
                      MATCHING_REQUEST_STATUSES.approved &&
                      'Approved on') ||
                      (MATCHING_REQUEST_STATUSES[matchingRequestStatus] ===
                        MATCHING_REQUEST_STATUSES.denied &&
                        'Denied on')}
                  </TableCell>
                )}
                <TableCell
                  size="medium"
                  style={{
                    width: 100,
                    textAlign: 'center',
                  }}
                >
                  <TableSortLabel
                    direction={fetchParams.get('order')}
                    active={fetchParams.get('sort') === 'amount'}
                    onClick={(): void => sortTableBy('amount', true)}
                  >
                    Donation
                  </TableSortLabel>
                </TableCell>
                {MATCHING_REQUEST_STATUSES[matchingRequestStatus] !==
                  MATCHING_REQUEST_STATUSES.denied && (
                  <TableCell
                    size="medium"
                    style={{
                      width: 100,
                      textAlign: 'center',
                    }}
                  >
                    <TableSortLabel
                      direction={fetchParams.get('order')}
                      active={fetchParams.get('sort') === relevantDonationMatchAmountField}
                      onClick={(): void => sortTableBy(relevantDonationMatchAmountField, true)}
                    >
                      {MATCHING_REQUEST_STATUSES[matchingRequestStatus] ===
                        MATCHING_REQUEST_STATUSES.pending && (
                        <Tooltip title="The best match value represents the maximum amount the company can match an employee’s match request, based on factors like applicable matching rules, per-user limits, and available budgets.">
                          <span>Best Match</span>
                        </Tooltip>
                      )}
                      {MATCHING_REQUEST_STATUSES[matchingRequestStatus] ===
                        MATCHING_REQUEST_STATUSES.approved && 'Matched'}
                    </TableSortLabel>
                  </TableCell>
                )}
                <TableCell size="medium">Details</TableCell>
                {showApproveDeny && <TableCell size="medium">Approve / Deny</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody
              style={loading ? { opacity: 0.3, cursor: 'wait', pointerEvents: 'none' } : null}
            >
              {!loading && error && (
                <TableRow>
                  <TableCell colSpan={showApproveDeny ? 7 : 6}>
                    <Typography>
                      There was an error loading your external donation requests
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!totalCount && !loading && !error && (
                <TableRow>
                  <TableCell colSpan={showApproveDeny ? 7 : 6}>
                    <Typography>
                      {MATCHING_REQUEST_STATUSES[matchingRequestStatus] ===
                        MATCHING_REQUEST_STATUSES.approved &&
                        `You have no approved external donation requests`}
                      {MATCHING_REQUEST_STATUSES[matchingRequestStatus] ===
                        MATCHING_REQUEST_STATUSES.denied &&
                        `You have no denied external donation requests`}
                      {MATCHING_REQUEST_STATUSES[matchingRequestStatus] ===
                        MATCHING_REQUEST_STATUSES.pending &&
                        `You have no pending external donation requests`}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {displayedDonations.map((donation: Donation) => (
                <TableRow key={donation.id}>
                  {showApproveDeny && (
                    <TableCell>
                      {statusUpdating.get(donation.id) ? (
                        <CircularProgress size={24} style={{ margin: '10px 12px' }} />
                      ) : (
                        <Checkbox
                          color="primary"
                          checked={selectedItems.includes(donation.id)}
                          onChange={() => handleCheckboxChange(donation.id)}
                          disabled={
                            anyStatusUpdating || !donation.matchableAmount || !donation.user?.email
                          }
                        />
                      )}
                    </TableCell>
                  )}
                  <TableCell>
                    {donation.user && (
                      <>
                        <Avatar
                          alt={donation.user?.name}
                          src={donation.user?.mainPicture?.replace('http://', 'https://')}
                          style={{
                            width: 32,
                            height: 32,
                            float: 'left',
                            marginRight: 10,
                          }}
                        />
                        <Link
                          to={`/volunteers/${donation.user?.id}`}
                          className="hoverable"
                          style={styles.nameLink}
                          onClick={onClickHandler}
                        >
                          {donation.user?.name}
                        </Link>
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    {donation.user?.location && allLocations.get(donation.user?.location)
                      ? `${[
                          allLocations.get(donation.user?.location)?.name,
                          allLocations.get(donation.user?.location)?.state,
                          allLocations.get(donation.user?.location)?.country,
                        ]
                          .filter(Boolean)
                          .join(', ')}`
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {donation.nonprofits?.map((nonprofit, i) => (
                      <React.Fragment key={nonprofit.id}>
                        <NonprofitLink nonprofit={nonprofit} />
                        {i + 1 < donation.nonprofits.size ? ', ' : ''}
                      </React.Fragment>
                    )) || '-'}
                  </TableCell>
                  {showAiScore && (
                    <TableCell>
                      {typeof donation.donationReceipt?.confidenceScore === 'number' && (
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => {
                            setSelectedDonationId(donation.id);
                            setModalOpen(true);
                          }}
                        >
                          <ConfidenceScoreChip
                            matchable={donation.get(relevantDonationMatchAmountField) > 0}
                            confidenceScore={donation.donationReceipt.confidenceScore}
                          />
                        </Button>
                      )}
                    </TableCell>
                  )}
                  <TableCell>{formatDateWithoutTimezone(donation.date)}</TableCell>
                  <TableCell>
                    {formatDonationDateForMatchingRequestStatus(donation, matchingRequestStatus)}
                  </TableCell>
                  <TableCell
                    style={{
                      width: 100,
                      textAlign: 'right',
                      fontFamily: 'Andale Mono, Courier, monospace',
                    }}
                  >
                    {currenciesFormat(donation.amountCurrencies, donation.currency)}
                  </TableCell>
                  {MATCHING_REQUEST_STATUSES[matchingRequestStatus] !==
                    MATCHING_REQUEST_STATUSES.denied && (
                    <TableCell
                      style={{
                        width: 110,
                        textAlign: donation.get(relevantDonationMatchAmountField)
                          ? 'right'
                          : 'left',
                        fontFamily: donation.get(relevantDonationMatchAmountField)
                          ? 'Andale Mono, Courier, monospace'
                          : undefined,
                      }}
                    >
                      {!(donation.get(relevantDonationMatchAmountField) > 0) && (
                        <Typography style={{ color: gray3, fontSize: 12 }}>Not Eligible</Typography>
                      )}
                      {donation.get(relevantDonationMatchAmountField) > 0 &&
                        (donation.get(relevantDonationMatchingRuleField) ? (
                          <Tooltip
                            title={`${donation.get(relevantDonationMatchingRuleField).title} · ${
                              donation.get(relevantDonationMatchingRuleField).matchingPercentage
                            }%`}
                          >
                            <span>
                              {currenciesFormat(
                                donation.get(relevantDonationMatchAmountCurrenciesField),
                                donation.currency
                              )}
                            </span>
                          </Tooltip>
                        ) : (
                          <span>
                            {currenciesFormat(
                              donation.get(relevantDonationMatchAmountCurrenciesField),
                              donation.currency
                            )}
                          </span>
                        ))}
                    </TableCell>
                  )}
                  <TableCell>
                    <ButtonLink
                      style={{
                        color: primaryBlue,
                        cursor: 'pointer',
                        outline: 'none',
                      }}
                      onClick={() => {
                        switch (matchingRequestStatus) {
                          case MATCHING_REQUEST_STATUS_DISPLAY_KEYS.approved:
                          case MATCHING_REQUEST_STATUS_DISPLAY_KEYS.denied: {
                            history.push(`/donations/${donation.id}`);
                            break;
                          }

                          case MATCHING_REQUEST_STATUS_DISPLAY_KEYS.pending:
                          default: {
                            setSelectedDonationId(donation.id);
                            setModalOpen(true);
                            break;
                          }
                        }
                      }}
                    >
                      Details
                    </ButtonLink>
                  </TableCell>
                  {showApproveDeny && (
                    <TableCell>
                      {statusUpdating.get(donation.id) ? (
                        <CircularProgress size={24} style={{ margin: '10px 12px' }} />
                      ) : (
                        <>
                          {!donation.matchableAmount || !donation.user?.email ? (
                            <Tooltip title="Cannot approve unmatchable requests">
                              <span>
                                <IconButton disabled>
                                  <CheckIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : (
                            <IconButton
                              title="Approve"
                              onClick={() => dispatch(approveAction([donation.id]))}
                            >
                              <CheckIcon />
                            </IconButton>
                          )}
                          <IconButton
                            title="Deny"
                            onClick={() => {
                              setRejectDonationIds([donation.id]);
                            }}
                          >
                            <BlockIcon />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell
                  size="medium"
                  colSpan={2}
                  style={{ color: '#0000008a', fontSize: '0.875rem' }}
                >
                  Total: {totalCount}
                  {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/anchor-is-valid */}
                  <a
                    style={{ marginLeft: 5, outline: 'none' }}
                    onClick={(e: React.MouseEvent<HTMLElement>): void => {
                      const a = e.target.tagName === 'A' ? e.target : e.target.parentElement;

                      if (!a.href) {
                        const query = {
                          export: true,
                          external: true,
                          matchingRequestStatus: fetchParams.get('matchingRequestStatus'),
                        };
                        const queryStr = Object.keys(query)
                          .map((k: string) => query[k] && `${k}=${query[k]}`)
                          .filter(Boolean)
                          .join('&');
                        fetch(`${config.apiUrl}/api/donations?${queryStr}`, {
                          method: 'GET',
                          headers: {
                            Authorization: Api.defaultHeaders.Authorization,
                          },
                        })
                          .then((response) => response.blob())
                          .then((blob) => {
                            const url = window.URL.createObjectURL(blob);
                            a.href = url;
                            a.download = `externalDonationRequests-${new Date().toISOString()}--${
                              MATCHING_REQUEST_STATUSES[matchingRequestStatus]
                            }.csv`;
                            a.click();
                            window.URL.revokeObjectURL(url);
                            a.removeAttribute('href');
                          });
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    title="Download CSV file"
                  >
                    Export
                  </a>
                </TableCell>
                <TablePagination
                  colSpan={100} // make the pagination stretch full width
                  count={totalCount}
                  rowsPerPage={fetchParams.get('limit')}
                  page={fetchParams.get('pageNum')}
                  onChangePage={goToPage}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  SelectProps={{
                    inputProps: { 'aria-label': 'rows per page' },
                    native: true,
                  }}
                  onChangeRowsPerPage={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableBox>
      </div>
      {selectedDonation && (
        <ExternalDonationRequestModal
          modalOpen={modalOpen}
          onApprove={() => dispatch(approveAction([selectedDonation.id]))}
          onDeny={() => {
            setRejectDonationIds([selectedDonation.id]);
            setModalOpen(false);
          }}
          selectedDonation={selectedDonation}
          setModalOpen={setModalOpen}
          statusUpdating={statusUpdating}
        />
      )}
      {rejectDonationIds.length > 0 && (
        <RejectExternalDonationModal
          multiple={rejectDonationIds.length > 1}
          onClose={() => setRejectDonationIds([])}
          onReject={({ rejectionReason }) => {
            dispatch(denyAction(rejectDonationIds, rejectionReason));
            setRejectDonationIds([]);
          }}
        />
      )}
    </>
  );
};

const withForm = reduxForm({
  form: FORM_IDENTIFIER,
  onChange: (values, dispatch, __, previousValues) => {
    if (values.size === previousValues.size) {
      dispatch(setFetchParamsAction({}));
    }
  },
  initialValues: {
    currencies: [],
    from: null,
    to: null,
    confidenceScore: null,
    amountGte: null,
    amountLte: null,
  },
  destroyOnUnmount: false,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
});

export default withForm(ExternalDonationRequests);
